import moment from "moment"
import { get, chain, size, some, first, last, map, pickBy, find, includes, isEmpty, head } from "lodash"
import { createSelector } from "reselect"
import { getFactorCategory, getFactorLabel, getIsAggregatedFactor, makeNonZeroKeysSelectorFromData, computeAvailableSelectViewFactors } from "helpers/factors"
import { aggregateExposuresFactors } from "helpers/factorClassification"
import { computeCategoryItemsSubset, computeNonZerosKeys, getAllCategoryItems as getAllCategoryItemsFn, getOrderedCategoryItems as getOrderedCategoryItemsFn } from "helpers/analyze"
import { makeComputeDriftFromData } from "helpers/drift"
import { METRIC_TYPES } from "helpers/assets/constants"
import {
  getCategories,
  getContentSets,
  getContentSetId,
  getDisabled,
  getFactorCategoryId,
  getOnly,
  getAggregationFunction,
  getMetricType,
} from "."

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

export const getExposureData = state => get(state, "global.data.compositeExposure.data.model.simulation.exposure", EMPTY_OBJECT)
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getAggregatedFactorsMetadata = (_, props) => props.aggregatedFactorsMetadata
const getFactorsMetadata = (_, props) => props.factorsMetadata
const getFavoriteFactors = (_, props) => props.favoriteFactors
const getSelectedViews = (_, props) => props.selectedViews
const getFactorsClassification = (_, props) => props.factorsClassification
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
const getStartDate = (_, props) => props.startDate
const getEndDate = (_, props) => props.endDate

function formatExposure(exposure, mustAggregateFactors, styleAggregation, selectedViewFactors, selectedViews, factorsClassification, metricType) {
  const count = size(exposure)
  const exposureField = metricType === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"
  return chain(exposure)
    .map(({ date, factors }, index) => {
      // Always return last date
      if (size(factors) || index === count - 1) {
        let exposureFactors = factors
        if (mustAggregateFactors) {
          exposureFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, exposureField)
        }

        if (size(selectedViews)) {
          exposureFactors = chain(factors)
            .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
            .map(factor => ({ ...factor, customView: true }))
            .concat(exposureFactors)
            .value()
        }

        return ({
          date,
          factors: chain(exposureFactors)
            .keyBy("id")
            .mapValues(exposureField)
            .value(),
        })
      }

      return null
    })
    .compact()
    .value()
}

export function computeData(items, exposure) {
  return chain(exposure)
    .map((datum, index) => {
      const { date } = datum

      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            typeof item.fn === "function" ? item.fn(datum) : get(datum, item.field, 0),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .compact()
    .value()
}

export const getExposure = createSelector(
  [getExposureData, getMustAggregateFactors, getAggregationFunction, getSelectedViewsFactors, getSelectedViews, getFactorsClassification, getMetricType],
  formatExposure,
)

const getNonZerosKeysForViewFactors = makeNonZeroKeysSelectorFromData({
  getter: value => get(value, "factors"),
  isAboveZeroTreshold: (value) => Math.abs(value) !== 0,
  dataSelector: getExposureData,
  favoriteSelector: getFavoriteFactors,
  factorValueField: "exposure",
})

export const getAvailableSelectViewFactors = createSelector(
  [getAggregatedFactorsMetadata, getFactorsClassification, getSelectedViews, getNonZerosKeysForViewFactors],
  computeAvailableSelectViewFactors,
)

export const getFirst = createSelector(
  [getExposure],
  first,
)

export const getCurrent = createSelector(
  [getExposure],
  last,
)

export const getFactors = createSelector(
  [getFactorsMetadata, getContentSets, getContentSetId],
  (factors, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        return chain(contentSet.factors)
          .keyBy("id")
          .mapValues(({ name, category }) => ({
            category, label: name,
          }))
          .value()
      }
      return null
    }

    return factors
  },
)

export const getAllItems = createSelector(
  [getFactors, getCurrent, getOnly, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, only, disabled, favoriteFactors) => map(factorsMetadata, (_, id) => ({
    id,
    label: getFactorLabel(factorsMetadata, id),
    category: getFactorCategory(factorsMetadata, id),
    favorite: get(favoriteFactors, id),
    lastValue: get(lastDatum, ["factors", id], 0),
    field: `factors.${id}`,
    disabled: only ? only !== id : includes(disabled, id),
    isAggregated: getIsAggregatedFactor(factorsMetadata, id),
    selectedView: factorsMetadata[id].selectedView,
  })),
)

export const getFilteredExposure = createSelector(
  [getExposure, getStartDate, getEndDate],
  (exposure, startDate, endDate) =>
    exposure.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      }
      return null
    }),
)

export const getFirstHistoricalExposureDate = createSelector(
  [getFilteredExposure],
  (exposure) => chain(exposure).first().get("date", null).value(),
)

export const getData = createSelector(
  [getAllItems, getFilteredExposure],
  computeData,
)

const getNonZerosKeys = createSelector(
  [getData, getFavoriteFactors],
  computeNonZerosKeys,
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  (items, keys) => chain(items)
    .filter(item => keys[item.id])
    .map(item => [item.category, item.selectedView])
    .flatten()
    .compact()
    .uniq()
    .value(),
)

export const getOptions = createSelector(
  [getCategories, getFirst, getCurrent, getDisplayCategories],
  (categories, first, current, displayCategories) => {
    if (first && current) {
      return categories.filter(category => includes(displayCategories, category.id))
    } else {
      return []
    }
  },
)

export const getDrift = createSelector(
  [getExposure],
  makeComputeDriftFromData(item => item.factors),
)

export const getSelectedOption = createSelector(
  [getOptions, getFactorCategoryId],
  (options, categoryId) => find(options, { id: categoryId }) ?? head(options),
)

export const getSelectedFactorCategoryId = createSelector(
  [getSelectedOption],
  (selectedOption) => get(selectedOption, "id"),
)

export const getSelectedFactorCategoryName = createSelector(
  [getSelectedOption],
  (selectedOption) => get(selectedOption, "label"),
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getSelectedFactorCategoryId, getAggregationFunction],
  getAllCategoryItemsFn,
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getSelectedFactorCategoryId, getAggregationFunction],
  getOrderedCategoryItemsFn,
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys, getCategoryItemsSubset],
  (data, keys, items) => isEmpty(items) ? EMPTY_ARRAY : map(data, item => pickBy(item, (_, key) => keys[key])),
)
